import { useState, useEffect, type CSSProperties } from 'react';
import { useSSRImageMappings } from '../contexts/SSRImageMappingsContext';
import { DEFAULT_IMAGE_MAPPINGS } from '../lib/imageDefaults.js';
import { focusToObjectPosition, isValidFocus, slotFocusKey } from '../lib/imageFocus';

declare global {
  interface Window {
    __INITIAL_IMAGE_MAPPINGS__?: Record<string, Record<string, string>>;
  }
}

interface ImageMappings {
  [page: string]: { [slot: string]: string };
}

// One list for the page, the server and the hero preload: src/lib/imageDefaults.js.
const DEFAULTS: ImageMappings = DEFAULT_IMAGE_MAPPINGS as ImageMappings;

let cachedMappings: ImageMappings | null = null;
let fetchPromise: Promise<ImageMappings> | null = null;
// Flipped to true the first time a hook instance has hydrated from the SSR
// global. Subsequent mounts (e.g. SPA navigation between pages that both
// use this hook) short-circuit the fetch path entirely.
let seededOnce = false;

function fetchMappings(): Promise<ImageMappings> {
  if (!fetchPromise) {
    fetchPromise = fetch('/api/image-mappings')
      .then((res) => (res.ok ? res.json() : DEFAULTS))
      .then((data) => {
        cachedMappings = data;
        return data;
      })
      .catch(() => {
        cachedMappings = DEFAULTS;
        return DEFAULTS;
      });
  }
  return fetchPromise;
}

export function useImageMappings() {
  const ssrMappings = useSSRImageMappings();
  // Initial state seeded from the highest-fidelity source available so the
  // first render — both server-side and client hydration — uses the env's
  // admin-uploaded images instead of the hardcoded DEFAULTS:
  //   1. SSR context (server render)
  //   2. window.__INITIAL_IMAGE_MAPPINGS__ (client hydration of prerendered HTML)
  //   3. cached fetch result (subsequent client navigation)
  //   4. DEFAULTS (dev / fallback)
  const [mappings, setMappings] = useState<ImageMappings>(() => {
    if (ssrMappings) return ssrMappings;
    if (typeof window !== 'undefined' && window.__INITIAL_IMAGE_MAPPINGS__) {
      return window.__INITIAL_IMAGE_MAPPINGS__;
    }
    return cachedMappings || DEFAULTS;
  });

  useEffect(() => {
    // First-mount seeding: cache the window-injected mappings once so other
    // consumers (and remounted instances after SPA navigation) short-circuit
    // the fetch path. We never flip the flag back — once seeded, always
    // seeded for this page-load lifecycle.
    if (typeof window !== 'undefined' && window.__INITIAL_IMAGE_MAPPINGS__) {
      if (!seededOnce) {
        cachedMappings = window.__INITIAL_IMAGE_MAPPINGS__;
        seededOnce = true;
      }
      return;
    }
    if (ssrMappings) {
      // SSR render path — nothing to do client-side.
      return;
    }
    if (cachedMappings) {
      setMappings(cachedMappings);
      return;
    }
    fetchMappings().then(setMappings);
    // ssrMappings is stable for this hook's lifetime; intentional empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getImage = (page: string, slot: string): string => {
    return mappings[page]?.[slot] || DEFAULTS[page]?.[slot] || '';
  };

  // The slot's crop position ("<x>% <y>%", stored next to its path as
  // "<slot>@focus"; see src/lib/imageFocus.ts) as an inline style for the
  // slot's <img>. `undefined` — not an empty object — when the slot has none:
  // the <img> then renders no style attribute at all, so every page without a
  // position is byte-identical to what it was before positions existed.
  // Honoured only next to a path of the slot's own: a position left behind by
  // an image that is gone (or that never replaced the seed) is ignored. Reads
  // the same `mappings` state as getImage, so server render and hydration agree.
  const getImageFocusStyle = (page: string, slot: string): CSSProperties | undefined => {
    const own = mappings[page]?.[slot];
    if (typeof own !== 'string' || own === '') return undefined;
    const focus = mappings[page]?.[slotFocusKey(slot)];
    if (typeof focus !== 'string' || focus === '' || !isValidFocus(focus)) return undefined;
    return { objectPosition: focusToObjectPosition(focus) };
  };

  return { getImage, getImageFocusStyle, mappings };
}
